import { useCallback, useState } from 'react';

import { createCurrentUserQueryGuard, ensureCurrentUserData } from '@futrli/views/queries/currentUser';
import { createProviderConnection, getCallbackUrl } from '@futrli/organisations';
import { getProductUrl, PREDICT_PRODUCT_SLUG } from '@futrli/shared-services';

/**
 * Hook to reconnect a datasource, accepting explicit IDs rather than using useParams().
 * This allows it to be used from any level in the component tree.
 */
export const useReconnectDatasourceByIds = (
  workspaceId: string,
  organisationId: string,
  datasource?: { id: string },
  integration?: { slug: string }
) => {
  const [isReconnecting, setIsReconnecting] = useState(false);

  const defaultSuccessUrl = getCallbackUrl(getProductUrl(workspaceId, organisationId, PREDICT_PRODUCT_SLUG));

  const reconnect = useCallback(
    async (successUrl: string = defaultSuccessUrl) => {
      if (organisationId && datasource && integration) {
        setIsReconnecting(true);

        try {
          const request = ensureCurrentUserData();
          const isCurrent = createCurrentUserQueryGuard();
          const currentUser = await request;
          if (!isCurrent()) {
            setIsReconnecting(false);
            return;
          }
          const connectionData = await createProviderConnection(workspaceId, organisationId, {
            workspace_id: workspaceId,
            organisation_id: organisationId,
            datasource_id: datasource.id,
            integration: integration.slug,
            user_id: currentUser.id,
            provider: integration.slug === 'xero' ? 'xero' : 'ledgerflow',
            success_url: successUrl
          });
          if (isCurrent()) {
            window.location.href = connectionData.initiation_url;
          }
        } catch (e) {
          setIsReconnecting(false);
        }
      }
    },
    [defaultSuccessUrl, datasource, integration, organisationId, workspaceId]
  );

  return {
    isReconnecting,
    reconnect
  };
};
